import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Lock, X } from 'lucide-react';
import { api } from '@/api';
import { setAuthToken, setStoredUser } from '@/utils/auth';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('form');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }
    
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid reset link');
      return;
    }

    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setStatus('success');
      setMessage('Password reset successful! You can now log in.');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800 px-4">
        <Card className="w-full max-w-md bg-gray-800/80 border-sky-900/30 text-center">
          <CardContent className="pt-8 pb-8">
            <Check className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Password Reset!</h2>
            <p className="text-gray-400 mb-6">{message}</p>
            <Button onClick={() => navigate('/admin')} className="bg-sky-400 hover:bg-sky-500 text-gray-900">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800 px-4">
      <Card className="w-full max-w-md bg-gray-800/80 border-sky-900/30">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-white">Reset Password</CardTitle>
        </CardHeader>
        <CardContent>
          {message && status === 'error' && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg mb-4">
              {message}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-300">New Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-gray-700 border-gray-600 text-white"
                required
                minLength={6}
              />
            </div>
            <div>
              <Label className="text-gray-300">Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-gray-700 border-gray-600 text-white"
                required
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full bg-sky-400 hover:bg-sky-500 text-gray-900" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Login (invite-only; new admins are added via Admins tab → Invite Admin,
// which sends the invitee a /setup?token=... link to set their own password).
const AdminAuth = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await api.login({ email: formData.email, password: formData.password });
      setAuthToken(res.data.access_token);
      setStoredUser(res.data.user);
      onLogin(res.data.user);
    } catch (err) {
      // Surface a specific error so the user (and their admin) know what actually failed.
      // "An error occurred" as a fallback was masking network / CORS / 500 issues.
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg;
      if (typeof detail === 'string' && detail) {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(d => d?.msg || JSON.stringify(d)).join(' — ');
      } else if (status) {
        msg = `Login failed (HTTP ${status}). Please contact your admin if this persists.`;
      } else if (err.message) {
        msg = `Network error: ${err.message}. Check your connection and try again.`;
      } else {
        msg = 'Unknown login error. Open DevTools → Network to inspect the failed request.';
      }
      setError(msg);
      console.error('Admin login failed:', { status, detail, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800 px-4">
      <Card className="w-full max-w-md bg-gray-800/80 border-sky-900/30">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-sky-400 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">
            Admin Login
          </CardTitle>
          <p className="text-gray-400">WPSL TV Admin</p>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg mb-4" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-300">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="admin@example.com"
                required
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <Label className="text-gray-300">Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="admin-login-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-sky-400 hover:bg-sky-500 text-gray-900"
              disabled={loading}
              data-testid="admin-login-submit"
            >
              {loading ? 'Please wait…' : 'Login'}
            </Button>
          </form>
          <p className="mt-6 text-xs text-gray-500 text-center">
            Admin accounts are invite-only. If you need access, ask an existing super-admin
            to invite you from the Admins tab.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Email Verification Page
const VerifyEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  
  const [status, setStatus] = useState(token ? 'verifying' : 'error');
  const [message, setMessage] = useState(token ? '' : 'Invalid verification link');

  useEffect(() => {
    if (!token) return;
    
    api.verifyEmail(token)
      .then(() => {
        setStatus('success');
        setMessage('Email verified successfully! You can now log in.');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed');
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800 px-4">
      <Card className="w-full max-w-md bg-gray-800/80 border-sky-900/30 text-center">
        <CardContent className="pt-8 pb-8">
          {status === 'verifying' && (
            <>
              <div className="animate-spin w-12 h-12 border-4 border-sky-400 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-white">Verifying your email...</p>
            </>
          )}
          {status === 'success' && (
            <>
              <Check className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Email Verified!</h2>
              <p className="text-gray-400 mb-6">{message}</p>
              <Button onClick={() => navigate('/admin')} className="bg-sky-400 hover:bg-sky-500 text-gray-900">
                Go to Login
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <X className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Verification Failed</h2>
              <p className="text-gray-400 mb-6">{message}</p>
              <Button onClick={() => navigate('/admin')} variant="outline" className="border-gray-600">
                Back to Login
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// ============== SETUP PASSWORD (admin invite acceptance) ==============
const SetupPassword = () => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('form'); // form | success | error
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const token = new URLSearchParams(location.search).get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { setStatus('error'); setMessage('Invalid invitation link.'); return; }
    if (password.length < 8) { setMessage('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setMessage('Passwords do not match.'); return; }
    setLoading(true);
    setMessage('');
    try {
      await api.setupPassword(token, password, name.trim() || undefined);
      setStatus('success');
      setMessage('Account activated! You can now log in.');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Setup failed.');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800 px-4">
        <Card className="w-full max-w-md bg-gray-800/80 border-sky-900/30 text-center" data-testid="setup-success">
          <CardContent className="pt-8 pb-8">
            <Check className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">You&apos;re all set!</h2>
            <p className="text-gray-400 mb-6">{message}</p>
            <Button onClick={() => navigate('/admin')} className="bg-sky-400 hover:bg-sky-500 text-gray-900" data-testid="setup-go-login">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800 px-4">
      <Card className="w-full max-w-md bg-gray-800/80 border-sky-900/30">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-white">Set Your Password</CardTitle>
          <p className="text-sm text-gray-400 mt-1">Welcome to WPSL TV admin. Pick a password to activate your account.</p>
        </CardHeader>
        <CardContent>
          {message && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg mb-4" data-testid="setup-error">
              {message}
            </div>
          )}
          {!token ? (
            <p className="text-red-300 text-sm">Missing invitation token in URL.</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Display name (optional)</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="How you'd like to appear in the admin panel"
                  className="bg-gray-700 border-gray-600 text-white"
                  data-testid="setup-name-input"
                />
              </div>
              <div>
                <Label className="text-gray-300">New Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                  minLength={8}
                  data-testid="setup-password-input"
                />
              </div>
              <div>
                <Label className="text-gray-300">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                  minLength={8}
                  data-testid="setup-confirm-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-sky-400 hover:bg-sky-500 text-gray-900"
                disabled={loading}
                data-testid="setup-submit"
              >
                {loading ? 'Activating…' : 'Activate Account'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// Team Name Autocomplete Component

export { ResetPassword, AdminAuth, VerifyEmail, SetupPassword };
